// ─── CANONICAL PER-ROUTE SEO REGISTRY (P4, static-in-code) ──────────────────
//
// Default and fallback layer of the hybrid SEO system. Every public content
// page renders <ManagedSeo path=...>, which reads the matching entry below,
// then applies a Firestore seoSettings override if one exists, falling back to
// these values so meta never blanks. The admin SEO Manager lints, previews, and
// edits the overrides; the values here are the in-code defaults, changed by a
// code edit plus a deploy.
//
// Follow-up (not in this batch): a build-time prerender so non-JS social
// crawlers see per-route meta at all.

import type { SeoEntry, SeoOgType } from '@advantage/shared-seo/metadata'
export { TITLE_MAX, TITLE_MIN, DESC_MAX, DESC_MIN, lintSeoEntry, computeDuplicates } from '@advantage/shared-seo/metadata'
export type { SeoEntry, SeoOgType, SeoLint } from '@advantage/shared-seo/metadata'

export const SEO_ROUTES: SeoEntry[] = [
  {
    path: '/store',
    label: 'Store',
    title: 'Halters & Rope Tack | Advantage Horsemanship Store',
    description: 'Explore handmade KYAR halters and rope reins, made by Scott Purdum’s father. Find sizing information and shop directly with Knot Your Average Rope.',
    ogType: 'website',
    hasSeoComponent: true,
  },
  {
    path: '/resources/spooking-under-saddle',
    label: 'Fixing Spooking Under Saddle',
    title: 'Fixing Spooking Under Saddle | Advantage Horsemanship',
    description: 'Respond to fear and distraction with clarity, patience, and better timing.',
    ogType: 'article',
    hasSeoComponent: true,
  },
  {
    path: '/resources/unwanted-behavior',
    label: 'Fixing Unwanted Behavior',
    title: 'Fixing Unwanted Behavior | Advantage Horsemanship',
    description: 'Look underneath the behavior. Find a clearer next step.',
    ogType: 'article',
    hasSeoComponent: true,
  },
  {
    path: '/resources/groundwork',
    label: 'The Groundwork Playbook',
    title: 'The Groundwork Playbook | Advantage Horsemanship',
    description: 'Six steps to a better horse and a clearer partnership.',
    ogType: 'article',
    hasSeoComponent: true,
  },
  {
    path: '/resources/rider-confidence',
    label: 'The Rider’s Confidence Playbook',
    title: 'The Rider’s Confidence Playbook | Advantage Horsemanship',
    description: 'Build calm, work through fear, and ride with clarity.',
    ogType: 'article',
    hasSeoComponent: true,
  },
  {
    path: '/resources/hindquarter-engagement',
    label: 'Hindquarter Engagement',
    title: 'Hindquarter Engagement | Advantage Horsemanship',
    description: 'Build reach, balance, and connection from the ground to the saddle.',
    ogType: 'article',
    hasSeoComponent: true,
  },
  {
    path: '/resources/herd-bound',
    label: 'Fixing Herd-Bound Behavior',
    title: 'Fixing Herd-Bound Behavior | Advantage Horsemanship',
    description: 'Build confidence in small moments away from the herd.',
    ogType: 'article',
    hasSeoComponent: true,
  },
  {
    path: '/resources/bucking',
    label: 'Fixing Bucking',
    title: 'Fixing Bucking | Advantage Horsemanship',
    description: 'Read the cause before you respond to the behavior.',
    ogType: 'article',
    hasSeoComponent: true,
  },
  {
    path: '/resources/treats-and-tension',
    label: 'Using Treats to Reduce Tension',
    title: 'Using Treats to Reduce Tension | Advantage Horsemanship',
    description: 'Use thoughtful placement and timing to support relaxation.',
    ogType: 'article',
    hasSeoComponent: true,
  },
  {
    path: '/resources/strong-back',
    label: 'The Strong Back Playbook',
    title: 'The Strong Back Playbook | Advantage Horsemanship',
    description: 'Understand the support underneath the back. Build quality before difficulty.',
    ogType: 'article',
    hasSeoComponent: true,
  },
  {
    path: '/resources/baggage-at-the-barn',
    label: 'The Baggage You Bring to the Barn',
    title: 'The Baggage You Bring to the Barn | Advantage Horsemanship',
    description: 'Notice what you bring into the interaction. Give yourself and your horse a clearer start.',
    ogType: 'article',
    hasSeoComponent: true,
  },

  {
    path: '/resources',
    label: 'Resource Library',
    title: 'Free Horsemanship Playbooks | Advantage Horsemanship',
    description: 'Practical guides from Scott Purdum for a clearer partnership with your horse. Explore free riding, behavior, confidence, and groundwork playbooks.',
    ogType: 'website',
    hasSeoComponent: true,
  },
  {
    path: '/resources/riders-foundation',
    label: 'Rider’s Foundation Playbook',
    title: 'Rider’s Foundation Playbook | Advantage Horsemanship',
    description: 'Build a balanced seat and clearer communication. Preview Scott Purdum’s five-step rider position guide, then unlock the full free playbook with your email.',
    ogType: 'article',
    hasSeoComponent: true,
  },
  {
    path: '/',
    label: 'Home',
    title: 'Horse Behaviorist & Rider Confidence Coach | Scott Purdum | Advantage Horsemanship',
    description: "Scott Purdum has helped over 60,000 riders fix the real issue. His full methodology is now available on demand in the Rider's Way App. Start your free 7-day trial.",
    ogType: 'website',
    hasSeoComponent: true,
  },
  {
    path: '/start',
    label: 'Start Here',
    title: 'Start Here | Advantage Horsemanship',
    description: 'Your horse is not the problem. Scott Purdum helps you fix the real issue. Start your free 7-day trial.',
    ogType: 'website',
    hasSeoComponent: true,
  },
  // NOTE: '/app' was registered here but NO SUCH ROUTE EXISTS in the router and
  // nothing on the site links to it. Found 2026-08-10 when the prerender produced
  // a completely empty page for it. Managing SEO for a page that 404s is worse
  // than not managing it, so it is removed until the app landing page is
  // actually built. Re-add this entry the day that route ships.
  {
    path: '/accelerator',
    label: 'Confidence Accelerator Class',
    title: 'Confidence Accelerator Class -- Live Weekly Coaching | Advantage Horsemanship',
    description: 'Live weekly Zoom coaching every Wednesday at 6pm EST. Build real confidence with your horse in 90 days. Small group, direct access to Scott Purdum.',
    ogType: 'product',
    hasSeoComponent: true,
  },
  {
    path: '/horse-sense',
    label: 'Horse Sense Coaching',
    title: 'Horse Sense Coaching Class | Learn How to Read Your Horse | Advantage Horsemanship',
    description: 'A live weekly coaching class with Scott Purdum. Learn how horses think, why they behave the way they do, and what you can do about it. Mondays at 6pm EST.',
    ogType: 'product',
    hasSeoComponent: true,
  },
  {
    path: '/masterclass',
    label: 'Riders Foundation Masterclass',
    title: 'Riders Foundation Masterclass -- 3-Day In-Person Coaching | Advantage Horsemanship',
    description: 'Three days of direct in-person coaching with Scott Purdum. You bring your horse. Scott builds a program around you. Ten riders maximum. When it fills, it closes.',
    ogType: 'event',
    hasSeoComponent: true,
  },
  {
    path: '/tour',
    label: 'Masterclass On Tour',
    title: 'The Confidence Accelerator Masterclass, On Tour | Advantage Horsemanship',
    description: 'Win three days of Advantage Horsemanship for your barn. Nominate your barn, rally your riders and auditors, and the barn that commits the most wins.',
    ogType: 'event',
    hasSeoComponent: true,
  },
  {
    path: '/tour/barns',
    label: 'Tour Barn Leaderboard',
    title: 'Barns in the Running | Masterclass On Tour | Advantage Horsemanship',
    description: 'See every barn nominated for the Confidence Accelerator Masterclass on tour. The barn that commits the most riders and auditors wins the event.',
    ogType: 'event',
    hasSeoComponent: true,
  },
  {
    path: '/private-sessions',
    label: 'Riding Intensive',
    title: 'Riding Intensive with Scott Purdum | Advantage Horsemanship',
    description: 'A 2-hour structured Riding Intensive with Scott Purdum, Horse Behaviorist and Rider Confidence Coach. $300 per session. Local in Fredericksburg VA or Scott travels to you.',
    ogType: 'service',
    hasSeoComponent: true,
  },
  {
    path: '/programs',
    label: 'All Programs',
    title: 'Horse Training Guides & Coaching Programs | Advantage Horsemanship',
    description: 'Browse self-paced horsemanship guides for rider confidence, groundwork, horse behavior, balance, and fitness, with paths into live coaching.',
    ogType: 'website',
    hasSeoComponent: true,
  },
  {
    path: '/schedule',
    label: 'Schedule',
    title: 'Schedule -- Events and Appearances | Advantage Horsemanship',
    description: 'Masterclasses, lectures, and expo appearances with Scott Purdum, Horse Behaviorist and Rider Confidence Coach. Find an event near you.',
    ogType: 'website',
    hasSeoComponent: true,
  },
  {
    path: '/about',
    label: 'About Scott',
    title: 'About Scott Purdum -- Horse Behaviorist & Rider Confidence Coach | Advantage Horsemanship',
    description: 'Scott Purdum is a horse behaviorist and rider confidence coach on a mission to help one million people become confident communicators with their horses.',
    ogType: 'profile',
    hasSeoComponent: true,
  },
  {
    path: '/contact',
    label: 'Contact',
    title: 'Contact Advantage Horsemanship | Scott Purdum',
    description: 'Get in touch with the Advantage Horsemanship team. Ask about programs, private sessions, masterclasses, or partnership opportunities.',
    ogType: 'website',
    hasSeoComponent: true,
  },

  // ── These routes were the last public pages wired to ManagedSeo. They now
  //    render the registry default below and apply any seoSettings override,
  //    the same as the others. Defaults are kept here so meta is never blank.
  {
    path: '/support',
    label: 'Support',
    title: 'Support | Advantage Horsemanship',
    description: "Get help with your Advantage Horsemanship account, the Rider's Way App, live classes, and the member portal. We are here when you need us.",
    ogType: 'website',
    hasSeoComponent: true,
  },
  {
    path: '/welcome',
    label: 'Welcome',
    title: 'Welcome | Advantage Horsemanship',
    description: "Welcome to Advantage Horsemanship. Get started with Scott Purdum's programs, live classes, and the Rider's Way App.",
    ogType: 'website',
    hasSeoComponent: true,
  },
  {
    path: '/programs/confident-equestrian',
    label: 'Confident Equestrian Guide',
    title: 'Confident Equestrian Guide | Advantage Horsemanship',
    description: 'A self-paced foundation guide from Scott Purdum to help you build calm, confident communication with your horse.',
    ogType: 'product',
    hasSeoComponent: true,
  },
  {
    path: '/programs/riders-foundation-guide',
    label: 'Riders Foundation Guide',
    title: 'Riders Foundation Guide | Advantage Horsemanship',
    description: "Scott Purdum's self-paced foundation guide for building a calm, willing partnership with your horse from the ground up.",
    ogType: 'product',
    hasSeoComponent: true,
  },
  {
    path: '/programs/groundwork-foundation-guide',
    label: 'Groundwork Foundation Guide',
    title: 'Groundwork Foundation Guide | Advantage Horsemanship',
    description: 'A self-paced groundwork guide from Scott Purdum to build trust, respect, and clear communication with your horse on the ground.',
    ogType: 'product',
    hasSeoComponent: true,
  },
  {
    path: '/programs/liberty-foundations',
    label: 'Liberty Foundations',
    title: 'Liberty Foundations | Advantage Horsemanship',
    description: "Scott Purdum's Liberty Foundations program. Build connection and willing communication with your horse at liberty, step by step.",
    ogType: 'product',
    hasSeoComponent: true,
  },
  {
    path: '/privacy-policy',
    label: 'Privacy Policy',
    title: 'Privacy Policy | Advantage Horsemanship',
    description:
      'How Advantage Horsemanship LLC collects, uses, and protects your information, including text message consent and mobile opt-in data.',
    ogType: 'article',
    hasSeoComponent: true,
  },
  {
    path: '/terms',
    label: 'Terms & Conditions',
    title: 'Terms & Conditions | Advantage Horsemanship',
    description:
      'Terms for Advantage Horsemanship clinics, live classes, and online programs, including text messaging terms, refunds, and assumption of risk.',
    ogType: 'article',
    hasSeoComponent: true,
  },
  {
    path: '/refund-policy',
    label: 'Refund Policy',
    title: 'Refund Policy | Advantage Horsemanship',
    description:
      "Advantage Horsemanship's refund and cancellation terms for clinics, live classes, and self-paced online programs, including the money back guarantee.",
    ogType: 'article',
    hasSeoComponent: true,
  },
  {
    path: '/text-updates',
    label: 'Text Updates Opt-In',
    title: 'Get Clinic Text Updates | Advantage Horsemanship',
    description:
      'Opt in to text messages from Advantage Horsemanship for clinic reminders, schedule changes, and day-of logistics. Optional, and you can stop any time.',
    ogType: 'service',
    hasSeoComponent: true,
  },
  {
    path: '/thank-you',
    label: 'Thank You (noindex)',
    title: 'Thank You | Advantage Horsemanship',
    description:
      'Thank you from Advantage Horsemanship. Your request has been received and Scott and the team will be in touch shortly.',
    ogType: 'website',
    hasSeoComponent: true,
  },
]

// Site-wide fallback used when a route has no registry entry at all (e.g. a
// dynamic route) or its entry somehow has a blank field. Guarantees meta is
// never empty.
export const DEFAULT_SEO: { title: string; description: string; ogType: SeoOgType } = {
  title: 'Advantage Horsemanship | Scott Purdum, Horse Behaviorist & Rider Confidence Coach',
  description:
    "Scott Purdum helps riders build real confidence with their horses through live classes, in-person masterclasses, self-paced guides, and the Rider's Way App.",
  ogType: 'website',
}

const SEO_BY_PATH: Record<string, SeoEntry> = Object.fromEntries(
  SEO_ROUTES.map((e) => [e.path, e]),
)

// Static registry lookup by route path. Undefined if the path is not managed.
export function getSeoEntry(path: string): SeoEntry | undefined {
  return SEO_BY_PATH[path]
}

// Firestore doc id for a route's override. Doc ids cannot contain '/', so the
// leading slash is dropped and inner slashes become '__'. '/' maps to 'root'.
export function routeKeyFor(path: string): string {
  if (path === '/') return 'root'
  return path.replace(/^\//, '').replace(/\//g, '__')
}
